import { Box, Stack, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useStores } from "mobx-stores/_root-store";
import { useCallback } from "react";
import {
  AddButton,
  ExportExcelButton,
} from "share-components/mui-wrap/button/action-button";
import { PageContentContainer } from "share-components/templates/page/page-content-container";
import { PageFilterContainer } from "share-components/templates/page/page-filter-container";
import { PageHeaderContainer } from "share-components/templates/page/page-header-container";

import { DeliveryNotesPermission } from "commons/enum/permission";
import { Dayjs } from "dayjs";
import { DateRange } from "share-components/atomics/date-range";
import { ProductGroupSelection } from "share-components/group-selection/product-group-selection/product-group-selection";
import { ProductGroupSelectionKey } from "share-components/group-selection/product-group-selection/use-product-group-selection";
import { XAutoCompleteWithPagination } from "share-components/mui-wrap/auto-complete/x-auto-complete-with-pagination";
import { SearchButton } from "share-components/mui-wrap/button/search-button";
import { XSearchTextField } from "share-components/mui-wrap/text-field/x-search-text-field";
import { AddDeliveryNote } from "../add-delivery-note/add-delivery-note";
import { ExportExcel } from "../export-excel/export-excel";
import { searchDeliveryNoteNumberOptionsWithPagination } from "./delivery-note-number-options";
import { DeliveryNoteTable } from "./delivery-note-table";

export const DeliveryNoteList = observer(() => {
  const {
    deliveryNoteManagement: {
      openNew,
      openExportExcel,
      getDeliveryNotes,
      pagination,
      queryParams: {
        dateRangeFilter: { fromAsDayJs, toAsDayJs, setProp: setDateRangeProps },
        name,
        dealingName,
        number,
        barcode,
        code,
        setProp: setQueryParamProp,
      },
    },
  } = useStores();
  const onSearch = useCallback(async () => {
    pagination.setProp("pageIndex", 0);
    await getDeliveryNotes();
  }, [getDeliveryNotes, pagination]);

  const fromDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("from", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const toDateChangedHandler = useCallback(
    (value?: Dayjs | null) => {
      setDateRangeProps("to", value ? value.toString() : null);
    },
    [setDateRangeProps],
  );

  const setProductFilterProp = useCallback(
    (prop: ProductGroupSelectionKey, value: string) => {
      setQueryParamProp(prop as "dealingName" | "name", value);
    },
    [setQueryParamProp],
  );

  return (
    <>
      <PageHeaderContainer direction={"row"} justifyContent={"space-between"}>
        <Header></Header>
      </PageHeaderContainer>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="start"
        gap={4}
      >
        <Stack direction="column" gap={2}>
          <PageFilterContainer>
            <DateRange
              label={"Ngày giao hàng"}
              fromDate={fromAsDayJs}
              toDate={toAsDayJs}
              onFromDateChanged={fromDateChangedHandler}
              onToDateChanged={toDateChangedHandler}
            ></DateRange>
            <Box sx={{ minWidth: "200px" }}>
              <XAutoCompleteWithPagination
                label="Số phiếu"
                value={number}
                searchFunc={searchDeliveryNoteNumberOptionsWithPagination}
                searchOnFocus={true}
                searchWhenRender={false}
                onValueChanged={(v) => {
                  setQueryParamProp("number", v?.value ?? "");
                }}
              />
            </Box>
          </PageFilterContainer>
          <PageFilterContainer>
            <ProductGroupSelection
              dealingNameSelected={dealingName}
              nameSelected={name}
              setSelectionProp={setProductFilterProp}
              searchWhenRender={false}
              codeSelected={code}
              visibleFields={["dealingName", "code", "name"]}
            ></ProductGroupSelection>
            <XSearchTextField
              label="Mã barcode"
              value={barcode}
              onChange={(e) =>
                setQueryParamProp("barcode", e.currentTarget.value)
              }
            />
            <SearchButton asyncAction={onSearch}></SearchButton>
          </PageFilterContainer>
        </Stack>
      </Stack>
      <PageContentContainer>
        <DeliveryNoteTable></DeliveryNoteTable>
      </PageContentContainer>

      <>{openNew && <AddDeliveryNote></AddDeliveryNote>}</>
      <>{openExportExcel && <ExportExcel></ExportExcel>}</>
    </>
  );
});

const Header = observer(() => {
  const {
    deliveryNoteManagement: { setProp },
  } = useStores();
  return (
    <>
      <Stack>
        <Typography variant="subtitle2">Quản lý phiếu giao hàng</Typography>
      </Stack>
      <Stack spacing={1} direction={"row"}>
        <ExportExcelButton
          accessPermission={[DeliveryNotesPermission.PrintDeliveryNote]}
          onClick={() => {
            setProp("openExportExcel", true);
          }}
        ></ExportExcelButton>
        <AddButton
          accessPermission={[DeliveryNotesPermission.Create]}
          onClick={() => {
            setProp("openNew", true);
          }}
        >
          Thêm mới
        </AddButton>
      </Stack>
    </>
  );
});
